import React, { useState, useEffect, useRef } from 'react';
import { Calendar, MapPin, Search } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';

const SHOWING_DAYS = [
  ['mon', 'Mon'],
  ['tue', 'Tue'],
  ['wed', 'Wed'],
  ['thu', 'Thu'],
  ['fri', 'Fri'],
  ['sat', 'Sat'],
  ['sun', 'Sun'],
];

function hoursToForm(hours) {
  const form = {};
  SHOWING_DAYS.forEach(([key]) => {
    const window = Array.isArray(hours?.[key]) ? hours[key][0] : '';
    const [start, end] = String(window || '').split('-');
    form[key] = {
      enabled: Boolean(start && end),
      start: start || '10:00',
      end: end || '16:00',
    };
  });
  return form;
}

function formToHours(form) {
  const hours = {};
  SHOWING_DAYS.forEach(([key]) => {
    const day = form[key];
    hours[key] = day?.enabled && day.start && day.end
      ? [`${day.start.slice(0, 5)}-${day.end.slice(0, 5)}`]
      : [];
  });
  return hours;
}

const STATUS_LABELS = {
  pending: 'Pending',
  confirmation_sent: 'Confirmation sent',
  confirmed: 'Confirmed',
  completed: 'Completed',
  no_show: 'No-show',
  cancelled: 'Cancelled',
  scheduled: 'Scheduled',
};

const STATUS_BADGE_CLASS = {
  pending: 'new',
  confirmation_sent: 'qualified',
  confirmed: 'active',
  completed: 'closed',
  no_show: 'closed',
  cancelled: 'closed',
  scheduled: 'active',
};

const STATUS_ACTIONS = {
  pending: [
    { status: 'confirmation_sent', label: 'Send confirmation', kind: 'secondary' },
    { status: 'confirmed', label: 'Confirmed by phone', kind: 'primary' },
    { status: 'cancelled', label: 'Cancel', kind: 'danger' },
  ],
  confirmation_sent: [{ status: 'confirmed', label: 'Mark confirmed', kind: 'primary' }, { status: 'cancelled', label: 'Cancel', kind: 'danger' }],
  confirmed: [{ status: 'completed', label: 'Mark done', kind: 'primary' }, { status: 'no_show', label: 'No-show', kind: 'danger' }, { status: 'cancelled', label: 'Cancel', kind: 'ghost' }],
  scheduled: [{ status: 'completed', label: 'Mark done', kind: 'primary' }, { status: 'no_show', label: 'No-show', kind: 'danger' }, { status: 'cancelled', label: 'Cancel', kind: 'ghost' }],
};

function actionsFor(appointment) {
  const actions = [...(STATUS_ACTIONS[appointment.status] || [])];
  const past = appointment.scheduled_at && new Date(appointment.scheduled_at).getTime() < Date.now();
  if (past && (appointment.status === 'pending' || appointment.status === 'confirmation_sent')) {
    actions.push({ status: 'completed', label: 'Mark done', kind: 'primary' });
    actions.push({ status: 'no_show', label: 'No-show', kind: 'danger' });
  }
  return actions;
}

export default function Appointments() {
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('id');
  const highlightRef = useRef(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [timeDrafts, setTimeDrafts] = useState({});
  const [filter, setFilter] = useState(() => (highlightId ? 'all' : 'requests'));
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [updatingId, setUpdatingId] = useState('');
  const [hoursForm, setHoursForm] = useState(null);
  const [openSlots, setOpenSlots] = useState([]);
  const [savingHours, setSavingHours] = useState(false);
  const [hoursMessage, setHoursMessage] = useState('');
  const [memoDrafts, setMemoDrafts] = useState({});
  const [memoBusy, setMemoBusy] = useState('');
  const [memoSaved, setMemoSaved] = useState({});

  useEffect(() => {
    api.get('/appointments/hours')
      .then((res) => {
        setHoursForm(hoursToForm(res.data.hours));
        setOpenSlots(res.data.slots || []);
      })
      .catch(() => setHoursMessage('Could not load showing hours.'));
  }, []);

  const saveHours = async () => {
    setSavingHours(true);
    setHoursMessage('');
    try {
      const res = await api.put('/appointments/hours', { hours: formToHours(hoursForm) });
      setHoursForm(hoursToForm(res.data.hours));
      setOpenSlots(res.data.slots || []);
      setHoursMessage('Saved. Ava will offer these times.');
    } catch (err) {
      setHoursMessage(err?.response?.data?.error || 'Could not save showing hours.');
    } finally {
      setSavingHours(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    const params =
      filter === 'scheduled' ? '?status=confirmed,scheduled'
        : filter === 'requests' ? '?status=pending,confirmation_sent'
          : '';
    setLoadError('');
    api.get(`/appointments${params}`)
      .then(res => setAppointments(res.data.appointments || []))
      .catch(() => {
        setAppointments([]);
        setLoadError('Could not load appointments.');
      })
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => {
    if (!highlightId || loading || !highlightRef.current) return;
    highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [highlightId, loading, appointments]);

  const rows = appointments.filter(a => {
    if (typeFilter && (a.appointment_type || 'showing') !== typeFilter) return false;
    if (statusFilter && a.status !== statusFilter) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const name = `${a.lead_first_name || ''} ${a.lead_last_name || ''}`.toLowerCase();
      const address = `${a.address_line1 || ''} ${a.city || ''}`.toLowerCase();
      if (!name.includes(q) && !address.includes(q)) return false;
    }
    if (filter === 'scheduled') {
      return a.scheduled_at && new Date(a.scheduled_at) >= new Date();
    }
    return true;
  });

  const saveShowingMemo = async (appointment) => {
    const transcript = String(memoDrafts[appointment.id] || '').trim();
    if (transcript.length < 8) return;
    setMemoBusy(appointment.id);
    try {
      await api.post('/listing-files/memos', {
        appointment_id: appointment.id,
        property_id: appointment.property_id || null,
        transcript,
      });
      setMemoDrafts((prev) => ({ ...prev, [appointment.id]: '' }));
      setMemoSaved((prev) => ({ ...prev, [appointment.id]: 'Saved to the listing file' }));
    } catch (err) {
      setMemoSaved((prev) => ({
        ...prev,
        [appointment.id]: err?.response?.data?.error || 'Could not save memo',
      }));
    } finally {
      setMemoBusy('');
    }
  };

  const updateAppointmentStatus = async (appointmentId, nextStatus) => {
    setUpdatingId(appointmentId);
    try {
      const payload = { status: nextStatus };
      if (nextStatus === 'confirmation_sent') payload.agent_confirmed = true;
      if (nextStatus === 'confirmed') payload.lead_confirmed = true;
      if (nextStatus === 'completed') {
        payload.agent_confirmed = true;
        payload.lead_confirmed = true;
      }

      const res = await api.patch(`/appointments/${appointmentId}`, payload);
      const updated = res.data.appointment;
      setAppointments((prev) => prev.map((a) => (a.id === appointmentId ? { ...a, ...updated } : a)));
    } catch (err) {
      // Keep this lightweight for demo flow while still surfacing failures.
      const data = err?.response?.data;
      const phone = data?.buyer_phone ? ` Their number is ${data.buyer_phone}.` : '';
      window.alert((data?.error || 'Failed to update appointment status') + phone);
    } finally {
      setUpdatingId('');
    }
  };

  const saveTime = async (appointmentId) => {
    const value = timeDrafts[appointmentId];
    if (!value) return;
    const scheduledAt = new Date(value);
    if (Number.isNaN(scheduledAt.getTime())) return;
    setUpdatingId(appointmentId);
    try {
      const res = await api.patch(`/appointments/${appointmentId}`, { scheduled_at: scheduledAt.toISOString() });
      const updated = res.data.appointment;
      setAppointments((prev) => prev.map((a) => (a.id === appointmentId ? { ...a, ...updated } : a)));
      setTimeDrafts((prev) => ({ ...prev, [appointmentId]: '' }));
    } catch (err) {
      window.alert(err?.response?.data?.error || 'Could not change the time');
    } finally {
      setUpdatingId('');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>Appointments</h2>
        <p>Showings, seller consultations, and meetings from your chat or lead flow.</p>
      </div>

      {hoursForm && (
        <div className="card" style={{ marginBottom: 16, padding: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Showing hours</div>
          <p style={{ margin: '0 0 12px', color: 'var(--text-muted)', fontSize: 14 }}>
            Newfoundland time. These are when you can attend. On each of your own listings, set the seller's hours. Ava offers the overlap, and skips times already booked.
          </p>
          <div style={{ display: 'grid', gap: 8 }}>
            {SHOWING_DAYS.map(([key, label]) => (
              <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={hoursForm[key].enabled}
                  onChange={(e) => setHoursForm((prev) => ({
                    ...prev,
                    [key]: { ...prev[key], enabled: e.target.checked },
                  }))}
                />
                <span style={{ width: 36 }}>{label}</span>
                <input
                  type="time"
                  className="form-input"
                  style={{ width: 130, padding: '6px 8px' }}
                  disabled={!hoursForm[key].enabled}
                  value={hoursForm[key].start}
                  onChange={(e) => setHoursForm((prev) => ({
                    ...prev,
                    [key]: { ...prev[key], start: e.target.value },
                  }))}
                />
                <span>to</span>
                <input
                  type="time"
                  className="form-input"
                  style={{ width: 130, padding: '6px 8px' }}
                  disabled={!hoursForm[key].enabled}
                  value={hoursForm[key].end}
                  onChange={(e) => setHoursForm((prev) => ({
                    ...prev,
                    [key]: { ...prev[key], end: e.target.value },
                  }))}
                />
              </label>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
            <button type="button" className="btn btn-primary btn-sm" disabled={savingHours} onClick={saveHours}>
              {savingHours ? 'Saving…' : 'Save hours'}
            </button>
            {hoursMessage && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{hoursMessage}</span>}
          </div>
          {openSlots.length > 0 && (
            <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--text-secondary)' }}>
              Next openings: {openSlots.slice(0, 4).map((s) => s.label).join(' · ')}
            </p>
          )}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="search-bar" style={{ flex: '1 1 200px', marginBottom: 0 }}>
          <Search size={16} color="var(--text-muted)" />
          <input placeholder="Search by name or address..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select
          className="form-input"
          style={{ width: 'auto', padding: '8px 12px' }}
          value={filter}
          onChange={e => setFilter(e.target.value)}
        >
          <option value="requests">Pending requests (showings & consults)</option>
          <option value="scheduled">Scheduled</option>
          <option value="all">All</option>
        </select>
        <select
          className="form-input"
          style={{ width: 'auto', padding: '8px 12px' }}
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
        >
          <option value="">All types</option>
          <option value="showing">Showings</option>
          <option value="consultation">Seller consultations</option>
        </select>
        <select
          className="form-input"
          style={{ width: 'auto', padding: '8px 12px' }}
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="pending">Pending</option>
          <option value="confirmation_sent">Confirmation sent</option>
          <option value="confirmed">Confirmed</option>
          <option value="completed">Completed</option>
          <option value="no_show">No-show</option>
          <option value="cancelled">Cancelled</option>
          <option value="scheduled">Scheduled</option>
        </select>
      </div>

      <div className="card">
        {loading ? (
          <div className="empty-state"><div className="spinner" /></div>
        ) : loadError ? (
          <div className="empty-state">
            <p>{loadError}</p>
          </div>
        ) : rows.length === 0 ? (
          <div className="empty-state">
            <Calendar size={32} color="var(--text-muted)" />
            <p>No {filter === 'requests' ? 'pending showing requests ' : filter === 'scheduled' ? 'scheduled appointments ' : ''}yet.</p>
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Type</th>
                  <th>Lead</th>
                  <th>Property</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(a => {
                  const isHighlighted = highlightId && String(a.id) === String(highlightId);
                  return (
                  <tr
                    key={a.id}
                    ref={isHighlighted ? highlightRef : undefined}
                    className={isHighlighted ? 'appointments-row--highlight' : undefined}
                  >
                    <td style={{ fontSize: 13 }}>
                      {a.scheduled_at
                        ? new Date(a.scheduled_at).toLocaleString(undefined, {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })
                        : '—'}
                      {a.is_overdue && (
                        <span
                          className="badge badge-overdue"
                          style={{ marginLeft: 6, fontSize: 10 }}
                          title="This date has already passed with no response"
                        >
                          Overdue
                        </span>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${a.appointment_type === 'consultation' ? 'seller' : 'buyer'}`}>
                        {a.appointment_type === 'consultation' ? 'Consultation' : 'Showing'}
                      </span>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {a.lead_first_name
                        ? `${a.lead_first_name} ${a.lead_last_name || ''}`.trim()
                        : '—'}
                      {a.lead_phone && (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{a.lead_phone}</div>
                      )}
                      {a.conversation_id && (
                        <div>
                          <Link to={`/chat?conv=${a.conversation_id}`} style={{ fontSize: 12 }}>Chat</Link>
                        </div>
                      )}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {a.address_line1 ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <MapPin size={11} />
                          {a.address_line1}{a.city ? `, ${a.city}` : ''}
                        </span>
                      ) : '—'}
                      {a.notes && (
                        <div style={{ marginTop: 4, color: 'var(--text-muted)', maxWidth: 280 }}>
                          {String(a.notes).slice(0, 160)}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${STATUS_BADGE_CLASS[a.status] || 'new'}`}>
                        {STATUS_LABELS[a.status] || a.status}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {actionsFor(a).map((action) => (
                          <button
                            key={action.status}
                            type="button"
                            className={`btn btn-sm btn-${action.kind}`}
                            disabled={updatingId === a.id}
                            onClick={() => updateAppointmentStatus(a.id, action.status)}
                          >
                            {action.label}
                          </button>
                        ))}
                        {actionsFor(a).length === 0 ? (
                          <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>No actions</span>
                        ) : null}
                        {['pending', 'confirmation_sent', 'confirmed', 'scheduled'].includes(a.status) && (
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 6 }}>
                            <input
                              type="datetime-local"
                              className="form-input"
                              style={{ width: 190, padding: '4px 8px', fontSize: 12 }}
                              value={timeDrafts[a.id] || ''}
                              onChange={(e) => setTimeDrafts((prev) => ({ ...prev, [a.id]: e.target.value }))}
                            />
                            <button
                              type="button"
                              className="btn btn-ghost btn-sm"
                              disabled={!timeDrafts[a.id] || updatingId === a.id}
                              onClick={() => saveTime(a.id)}
                            >
                              Save time
                            </button>
                          </div>
                        )}
                        {a.appointment_type !== 'consultation' && (
                          <div style={{ marginTop: 8, display: 'grid', gap: 6, minWidth: 180 }}>
                            <textarea
                              className="form-input"
                              rows={2}
                              placeholder="After-showing memo"
                              value={memoDrafts[a.id] || ''}
                              onChange={(e) => setMemoDrafts((prev) => ({ ...prev, [a.id]: e.target.value }))}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-secondary"
                              disabled={memoBusy === a.id || String(memoDrafts[a.id] || '').trim().length < 8}
                              onClick={() => saveShowingMemo(a)}
                            >
                              {memoBusy === a.id ? 'Saving…' : 'Save memo'}
                            </button>
                            {memoSaved[a.id] && (
                              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{memoSaved[a.id]}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
